import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { LatLng } from "leaflet";
import { useAddressStore } from "@utils/store/utils/useAddressStore";
import { addressToGeocode, combinedAddress, getCurrentLocation, haversineDistance } from "./func/addressTool";
import useDebounce from "../../hooks/useDebounce";
import toast from "react-hot-toast";
import { handleAPIGeocodeGetAddress } from "./API/handleAddressAPI";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { FieldValues, UseFormSetValue, Path } from "react-hook-form";

interface Geocode {
  lat: number;
  lng: number;
}

interface FormFields {
  city?: string;
  area?: string;
  road?: string;
  streetNumber?: string;
  floor?: string;
  room?: string;
  longitude?: number | null;
  latitude?: number | null;
}

interface MapPickerProps<T extends FieldValues> {
  setValue: UseFormSetValue<T>;
  formFields: FormFields;
  /** 地圖拖動時的提示文字，有值則顯示（例如「移動來修改地址」），不傳或空字串則不顯示 */
  moveHintText?: string;
  /** 使用者手動拖動地圖且距離在限制內時觸發（用於外部判斷 A/B 路徑） */
  onMapMoved?: () => void;
  /** dragWarning 狀態變更時通知外部（null = 無警告；string = 有超距警告） */
  onDragWarningChange?: (warning: string | null) => void;
  /** 定位進行中狀態變更時通知外部（true = 依地址取得座標中或 GPS 定位中），供外層卡控完成按鈕 */
  onGeocodingChange?: (isGeocoding: boolean) => void;
  /**
   * 未拆欄位的地址全文。給「地址是單一輸入框」的畫面（ConfirmAddressSheet／NpAddress）用：
   * 那些畫面的 formFields.city/area 永遠是空的，沒有這個就無從 geocode。
   * 有拆好的 city/area 時以 formFields 為準，此值僅作為沒有欄位時的來源。
   */
  addressText?: string;
}

interface MapCenterListenerProps {
  onCenterChange: (center: LatLng) => void;
}

//@ {sub} 監聽地圖拖拽（dragend：只有使用者手動拖動才觸發，程式呼叫 setView 不會觸發）
const MapCenterListener: React.FC<MapCenterListenerProps> = ({ onCenterChange }) => {
  useMapEvents({
    dragend: (e) => {
      const map = e.target;
      const center = map.getCenter();
      onCenterChange(center);
    },
  });
  return null;
};

/** 依經緯度更新地圖視角（react-leaflet v4 須在 MapContainer 內用 useMap() 取得 map 實例，ref 無法取得） */
const MapViewUpdater: React.FC<{ center: Geocode; zoom?: number }> = ({ center, zoom = 18 }) => {
  const map = useMap();
  useEffect(() => {
    map.setView([center.lat, center.lng], zoom);
  }, [map, center.lat, center.lng, zoom]);
  return null;
};

//@ {main} 地圖選擇器
//* @param - formFields - 取得此表單欄位值
const MapPicker = <T extends FieldValues>({ formFields, setValue, moveHintText, onMapMoved, onDragWarningChange, onGeocodingChange, addressText }: MapPickerProps<T>) => {
  const { lineAccessToken } = useLineAuth();
  const { setAutoCompleteAddress, mapPickerDisabled } = useAddressStore();
  const debugEnabled = process.env.NODE_ENV !== "production";
  const debugLog = (...args: unknown[]) => {
    if (!debugEnabled) return;
    console.log("[AddressDebug][MapPicker]", ...args);
  };


  //-- 經緯度儲存器（初始值優先吃 formFields，避免有 initialValues 時地圖跑到台灣中心）
  const [geocode, setGeocode] = useState<Geocode>(() => {
    const lat = Number(formFields.latitude);
    const lng = Number(formFields.longitude);
    return !Number.isNaN(lat) && !Number.isNaN(lng) && formFields.latitude != null && formFields.longitude != null
      ? { lat, lng }
      : { lat: 23.5, lng: 121.0 };
  });
  const debouncedGeocode = useDebounce(geocode, 300);
  /** 地址無法取得經緯度時顯示的錯誤訊息（紅字，地圖下方） */
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  /** 地圖拖拉超出 500m 時顯示的警告（紅字，地圖下方） */
  const [dragWarning, setDragWarning] = useState<string | null>(null);
  /** 初始座標（僅初始化時從 formFields 讀取一次，用於限制拖拉距離） */
  const originalGeocodeRef = useRef<{ lat: number; lng: number } | null>(
    (() => {
      const lat = Number(formFields.latitude);
      const lng = Number(formFields.longitude);
      return formFields.latitude != null && formFields.longitude != null && !Number.isNaN(lat) && !Number.isNaN(lng)
        ? { lat, lng }
        : null;
    })(),
  );
  /** 是否正在依地址取得經緯度（顯示「正在取得座標…」提示） */
  const [isGeocodingAddress, setIsGeocodingAddress] = useState(false);
  /** 是否正在取得目前位置（顯示「正在定位…」提示） */
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  /** 最新一次 geocode 請求的 id，用來丟棄已過期的非同步結果 */
  const geocodeRequestIdRef = useRef(0);
  /** 最新的 formFields ref，讓 Effect 在不加入依賴的情況下讀取最新值 */
  const formFieldsRef = useRef(formFields);
  useEffect(() => { formFieldsRef.current = formFields; });
  /**
   * 目前這組 lat/lng 是「哪一個地址」的座標。
   * 用來分辨兩種都有座標的情況：
   *   1. 座標與地址相符（autocomplete 帶入、或使用者拖曳微調同一個地址）→ 不要重新 geocode，否則會蓋掉微調結果
   *   2. 座標是舊地址的（拖曳後又改了門牌／路名）→ 必須重新 geocode，否則 pin 會停在舊位置且無提示
   */
  const coordsAddressRef = useRef<string | null>(
    (() => {
      const { city: c, area: a, road: r, streetNumber: sn, latitude: lat, longitude: lng } = formFields;
      if (lat == null || lng == null || !c || !a) return null;
      return combinedAddress({ city: c, area: a, road: r || "", streetNumber: sn || "" });
    })(),
  );
  /** 依當下表單欄位組出 geocode 用的地址字串（與 addressForGeocode 同一套規則） */
  const currentAddressString = () => {
    const { city: c, area: a, road: r, streetNumber: sn } = formFieldsRef.current;
    if (c && a) return combinedAddress({ city: c, area: a, road: r || "", streetNumber: sn || "" });
    return (addressTextRef.current || "").trim();
  };
  /** addressText 的最新值（供不在依賴陣列內的 callback 讀取） */
  const addressTextRef = useRef(addressText);
  useEffect(() => { addressTextRef.current = addressText; });

  //-- 地圖控制器項目
  const mapConfig = useMemo(
    () => ({
      dragging: !mapPickerDisabled,
      zoomControl: !mapPickerDisabled,
      scrollWheelZoom: !mapPickerDisabled,
      doubleClickZoom: !mapPickerDisabled,
      touchZoom: !mapPickerDisabled,
      boxZoom: !mapPickerDisabled,
      keyboard: !mapPickerDisabled,
    }),
    [mapPickerDisabled],
  );

  //-- 回到初始座標
  const handleResetToOriginal = () => {
    if (!originalGeocodeRef.current) return;
    const { lat, lng } = originalGeocodeRef.current;
    debugLog("reset-to-original", { lat, lng });
    setDragWarning(null);
    onDragWarningChange?.(null);
    setGeocode({ lat, lng });
    setValue("latitude" as Path<T>, lat as unknown as any, { shouldDirty: true, shouldTouch: true });
    setValue("longitude" as Path<T>, lng as unknown as any, { shouldDirty: true, shouldTouch: true });
  };

  //-- 手動拖拽地圖處理
  const handleCenterChange = (center: LatLng) => {
    debugLog("dragend-received", {
      center: { lat: center.lat, lng: center.lng },
      currentGeocode: geocode,
      originalGeocode: originalGeocodeRef.current,
      mapPickerDisabled,
    });
    setGeocodeError(null);
    setDragWarning(null);
    onDragWarningChange?.(null);
    const centerLat = center.lat.toFixed(4);
    const centerLng = center.lng.toFixed(4);
    if (centerLat === geocode.lat.toFixed(4) && centerLng === geocode.lng.toFixed(4)) return;

    // 距離限制：有初始座標時才檢查（新增/修改皆適用）
    if (originalGeocodeRef.current) {
      const dist = haversineDistance(
        originalGeocodeRef.current.lat, originalGeocodeRef.current.lng,
        Number(centerLat), Number(centerLng),
      );
      if (dist > 500) {
        const warning = "此位置與原地址相距超過 500 公尺，無法選擇此地點";
        debugLog("drag-blocked-by-distance", {
          distanceMeters: dist,
          originalGeocode: originalGeocodeRef.current,
          attemptedCenter: { lat: Number(centerLat), lng: Number(centerLng) },
        });
        setDragWarning(warning);
        onDragWarningChange?.(warning);
        // 強制 re-render 讓 MapViewUpdater 把地圖彈回原 geocode
        setGeocode((prev) => ({ ...prev }));
        return;
      }
    }

    // 更新座標，但不更新地址欄位（移除 setAutoCompleteAddress）
    debugLog("drag-accepted-set-latlng", {
      nextLat: Number(centerLat),
      nextLng: Number(centerLng),
    });
    setGeocode({ lat: Number(centerLat), lng: Number(centerLng) });
    setValue("latitude" as Path<T>, Number(centerLat) as any, { shouldDirty: true, shouldTouch: true });
    setValue("longitude" as Path<T>, Number(centerLng) as any, { shouldDirty: true, shouldTouch: true });
    // 拖曳是「針對目前地址」的微調 → 記下所屬地址，之後地址沒變就不會被 geocode 蓋掉
    coordsAddressRef.current = currentAddressString();
    onMapMoved?.();
  };

  //-- 判斷是否需要定位取得當前位置
  const handleAPIGetCurrentLocation = async () => {
    setIsGettingLocation(true);
    //API - 取得使用者目前定位
    let res = await getCurrentLocation();
    if (res.state === "success") {
      setIsGettingLocation(false);
      setGeocode({ lat: Number(res.lat), lng: Number(res.lng) });
      //API - 座標取得地址
      const geocodeGetAddressRes = await handleAPIGeocodeGetAddress({ lat: Number(res.lat), lng: Number(res.lng) }, lineAccessToken || "");
      if (geocodeGetAddressRes?.state === "success" && geocodeGetAddressRes?.possibleList?.length > 0) {
        setAutoCompleteAddress(geocodeGetAddressRes?.possibleList[0] as Record<string, any>);
      }
    } else {
      setIsGettingLocation(false);
      res?.errorMsg && toast.error(res?.errorMsg);
    }
  };

  const { city, area, road, streetNumber, latitude, longitude } = formFields;

  //-- 用於 geocode 的地址字串，防抖後才打 API，避免每打一個字就重新定位地圖
  const addressForGeocode = useMemo(
    () =>
      city && area
        ? combinedAddress({
            city,
            area,
            road: road || "",
            streetNumber: streetNumber || "",
          })
        : (addressText || "").trim(),
    [city, area, road, streetNumber, addressText],
  );
  const debouncedAddressForGeocode = useDebounce(addressForGeocode, 400);

  //-- Effect A：lat/lng 從外部寫入時（autocomplete、initialValues）同步地圖位置
  //   不依賴 debouncedAddressForGeocode，避免地址輸入時互相干擾
  //   同時補設 originalGeocodeRef（GPS 路徑 lat/lng 在 mount 後才到，需在此補設）
  useEffect(() => {
    if (latitude == null || longitude == null) return;
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    // GPS / autocomplete 路徑：lat/lng 在 mount 後才寫入，originalGeocodeRef 此時仍為 null → 補設
    if (originalGeocodeRef.current === null) {
      originalGeocodeRef.current = { lat, lng };
      debugLog("original-geocode-initialized", { lat, lng });
    }
    if (lat !== geocode.lat || lng !== geocode.lng) {
      debugLog("external-latlng-synced-to-map", { lat, lng });
      setGeocode({ lat, lng });
      // 外部改寫座標＝地址換了（autocomplete／呼叫端依地址 geocode）→ 500m 拖曳上限的基準點要跟著換。
      // 否則基準會停在最初的定位點（例：前一關 GPS 抓到台北，使用者把地址改成台中），
      // pin 跟著地址移過去就被自己的防呆擋成「相距超過 500 公尺」。
      // 拖曳不會走到這裡：拖曳的 handler 先 setGeocode 再 setValue，回流時 lat/lng 已相等而跳過。
      originalGeocodeRef.current = { lat, lng };
      // 外部帶入的座標屬於「當下這個地址」（autocomplete 會同時寫入地址欄位與 lat/lng）
      coordsAddressRef.current = currentAddressString();
      // 基準換了，先前那句「相距超過 500 公尺」已不成立 → 清掉，否則警告會殘留在新地址上
      setDragWarning(null);
      debugLog("original-geocode-rebased", { lat, lng });
    }
  }, [latitude, longitude]);

  //-- 依「目前表單地址」重新定位。Effect B（自動）與「依地址定位」按鈕（手動）共用同一條路徑。
  const runAddressGeocode = useCallback(async () => {
    const { city: c, area: a, road: r, streetNumber: sn } = formFieldsRef.current;
    const fullAddress = currentAddressString();
    if (!fullAddress) return;

    // fallback：路名層級（不含門牌），供找不到具體門牌時降級定位。
    // 單欄地址（無拆好的 city/area）沒有可靠的路名層級可退，留空即可。
    const roadLevelAddress = c && a && r ? combinedAddress({ city: c, area: a, road: r, streetNumber: "" }) : "";
    const requestId = ++geocodeRequestIdRef.current;

    const applyGeocodeResult = (nextLat: number, nextLng: number) => {
      setGeocode({ lat: nextLat, lng: nextLng });
      setValue("latitude" as Path<T>, nextLat as any, { shouldDirty: true, shouldTouch: true });
      setValue("longitude" as Path<T>, nextLng as any, { shouldDirty: true, shouldTouch: true });
      // 這組座標屬於剛剛 geocode 的地址
      coordsAddressRef.current = fullAddress;
      // 地址換了 → 500m 拖曳上限的基準點要跟著移到新地址，
      // 否則會被「相距超過 500 公尺」擋住（Effect A 在此路徑不會觸發：先 setGeocode 再 setValue，回流時已相等）
      originalGeocodeRef.current = { lat: nextLat, lng: nextLng };
      setDragWarning(null);
      onDragWarningChange?.(null);
      setGeocodeError(null);
    };

    debugLog("address-geocode-start", { fullAddress, roadLevelAddress });
    setIsGeocodingAddress(true);
    setGeocodeError(null);

    //-- addressToGeocode 內部已是「自家 address API → OSM fallback」，此處不再重複處理來源
    let geoRes = await addressToGeocode(fullAddress);
    if (
      (geoRes?.state !== "success" || Number.isNaN(Number(geoRes.lat)) || Number.isNaN(Number(geoRes.lng))) &&
      roadLevelAddress &&
      roadLevelAddress !== fullAddress
    ) {
      geoRes = await addressToGeocode(roadLevelAddress);
    }

    // 已有更新的請求 → 丟棄這次的結果（也不要動 loading，交給最新那次收尾）
    if (requestId !== geocodeRequestIdRef.current) return;

    if (geoRes?.state === "success") {
      const nextLat = Number(geoRes.lat);
      const nextLng = Number(geoRes.lng);
      if (!Number.isNaN(nextLat) && !Number.isNaN(nextLng)) {
        debugLog("address-geocode-success", { fullAddress, nextLat, nextLng });
        applyGeocodeResult(nextLat, nextLng);
        setIsGeocodingAddress(false);
        return;
      }
    }

    debugLog("address-geocode-failed", { fullAddress, roadLevelAddress, result: geoRes });
    setGeocodeError("找不到此地址的座標");
    setIsGeocodingAddress(false);
  }, [setValue, onDragWarningChange]);

  //-- Effect B：address 輸入完成（防抖後）才 geocode；
  //   透過 formFieldsRef 讀取當下 lat/lng 而不加入依賴，
  //   避免「清空 lat/lng → effect 立刻以舊 address 觸發多餘 geocode」的閃爍問題
  useEffect(() => {
    if (!debouncedAddressForGeocode) {
      setIsGeocodingAddress(false);
      setGeocodeError(null);
      return;
    }

    const { latitude: lat, longitude: lng, city: c, area: a } = formFieldsRef.current;

    // 自動定位的門檻：必須是拆好欄位的表單（有 city + area）。
    // 單欄地址畫面（ConfirmAddressSheet／NpAddress）打字過程中會產生大量半截地址，
    // 每次 geocode 都是付費的外部 API，那些畫面一律不自動打，只留「依地址定位」按鈕。
    // 門牌號不設為前提：路名層級也能先把 pin 帶到大致位置。
    if (!c || !a) {
      debugLog("skip-address-geocode-incomplete", { city: c, area: a });
      setIsGeocodingAddress(false);
      return;
    }

    // 已有座標，且座標就是「這個地址」的（autocomplete 帶入或使用者拖曳微調）→ 不重複 geocode，
    // 否則會把使用者拖曳的微調結果蓋掉。
    // 但若地址在取得座標之後被改過（拖完地圖再改門牌／路名），座標已是舊地址的 → 必須重新定位。
    if (lat != null && lng != null && coordsAddressRef.current === debouncedAddressForGeocode) {
      debugLog("skip-address-geocode-existing-latlng", { lat, lng, address: debouncedAddressForGeocode });
      setIsGeocodingAddress(false);
      setGeocodeError(null);
      return;
    }

    runAddressGeocode();
    // lineAccessToken 納入依賴：token 較晚就緒時要能重跑（座標已對應到同一地址則會在上方早退）
  }, [debouncedAddressForGeocode, lineAccessToken, runAddressGeocode]);

  //-- 把「定位中」狀態往上通知，讓外層的完成／儲存按鈕可以卡控
  useEffect(() => {
    onGeocodingChange?.(isGeocodingAddress || isGettingLocation);
  }, [isGeocodingAddress, isGettingLocation]);

  return (
    <div style={{ position: "relative", width: "100%", zIndex: 1, background: "#000" }}>
      <div style={{ height: "250px", width: "100%" }}>
      <MapContainer
        center={[geocode.lat, geocode.lng]}
        zoom={7}
        style={{ height: "100%", width: "100%" }}
        attributionControl={true}
        {...mapConfig}>
        <TileLayer
          url="https://wmts.nlsc.gov.tw/wmts/EMAP/default/GoogleMapsCompatible/{z}/{y}/{x}"
          attribution='&copy; <a href="https://maps.nlsc.gov.tw/">國土測繪中心</a>'
          maxZoom={19}
        />
        <MapViewUpdater center={debouncedGeocode} zoom={18} />
        <MapCenterListener onCenterChange={handleCenterChange} />
      </MapContainer>
      {/* 拖動地圖提示 - 氣泡＋底部三角指向地標；dragWarning 時切換為紅色警告樣式 */}
      {(moveHintText || dragWarning) ? (() => {
        const isWarning = !!dragWarning;
        const bubbleText = dragWarning ?? moveHintText;
        const bgColor = isWarning ? "#c0392b" : "#2C5F7F";
        return (
          <div
            style={{
              position: "absolute",
              left: "50%",
              ...(isWarning ? { top: "8px" } : { bottom: "calc(50% + 52px)" }),
              transform: "translateX(-50%)",
              zIndex: 1000,
              pointerEvents: isWarning ? "auto" : "none",
              color: "#fff",
              fontSize: 13,
              fontWeight: 500,
              backgroundColor: bgColor,
              padding: "8px 14px",
              borderRadius: 8,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              maxWidth: "80%",
              textAlign: "center",
              whiteSpace: "pre-wrap",
            }}>
            {bubbleText}
            {isWarning && (
              <button
                onClick={handleResetToOriginal}
                style={{
                  display: "block",
                  marginTop: 8,
                  width: "100%",
                  padding: "4px 10px",
                  background: "rgba(255,255,255,0.25)",
                  border: "1px solid rgba(255,255,255,0.6)",
                  borderRadius: 6,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}>
                回到原始位置
              </button>
            )}
            {/* 底部中央小三角指向地標 */}
            <div
              style={{
                position: "absolute",
                left: "50%",
                bottom: -6,
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: `6px solid ${bgColor}`,
              }}
            />
          </div>
        );
      })() : null}
      {/* 依地址定位 button：左下角，依目前表單填的地址重新取得座標（與右下角「使用目前位置」成對） */}
      {!mapPickerDisabled && (
        <button
          type="button"
          onClick={() => runAddressGeocode()}
          disabled={isGeocodingAddress || !addressForGeocode}
          title="依目前填寫的地址重新定位"
          style={{
            position: "absolute",
            bottom: 12,
            left: 12,
            zIndex: 1000,
            height: 36,
            padding: "0 12px",
            borderRadius: 18,
            background: "#fff",
            border: "1px solid #ccc",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            fontWeight: 600,
            color: addressForGeocode ? "#0281C8" : "#9ca3af",
            cursor: isGeocodingAddress || !addressForGeocode ? "not-allowed" : "pointer",
          }}
        >
          {isGeocodingAddress ? (
            <div className="animate-spin" style={{ width: 16, height: 16, border: "2px solid #60a5fa", borderTopColor: "transparent", borderRadius: "50%" }} />
          ) : (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M17.657 5.304c-3.124-3.073-8.189-3.073-11.313 0-3.124 3.074-3.124 8.057 0 11.13l5.656 5.565 5.657-5.565c3.124-3.073 3.124-8.056 0-11.13zm-5.657 8.195c-.668 0-1.295-.26-1.768-.732-.975-.975-.975-2.561 0-3.536.472-.472 1.1-.732 1.768-.732s1.296.26 1.768.732c.975.975.975 2.562 0 3.536-.472.472-1.1.732-1.768.732z" />
            </svg>
          )}
          {isGeocodingAddress ? "定位中…" : "依地址定位"}
        </button>
      )}
      {/* GPS 定位 button：右下角浮動，點擊取得目前位置並帶入地址欄位 */}
      {!mapPickerDisabled && (
        <button
          type="button"
          onClick={handleAPIGetCurrentLocation}
          disabled={isGettingLocation}
          title="使用目前位置"
          style={{
            position: "absolute",
            bottom: 12,
            right: 12,
            zIndex: 1000,
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "#fff",
            border: "1px solid #ccc",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isGettingLocation ? "not-allowed" : "pointer",
          }}
        >
          {isGettingLocation ? (
            <div className="animate-spin" style={{ width: 18, height: 18, border: "2px solid #60a5fa", borderTopColor: "transparent", borderRadius: "50%" }} />
          ) : (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="#3b82f6">
              <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
            </svg>
          )}
        </button>
      )}
      {/* 地標 icon：圖釘尖端對齊地圖中心（與下方提示文字箭頭同一個座標點） */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, calc(-100% + 12px))",
          zIndex: 1000,
          pointerEvents: "none",
        }}>
        <svg
          fill="#0281C8"
          width="40"
          height="40"
          viewBox="0 0 24 24"
          style={{ display: "block", filter: "drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.4))" }}>
          <path d="M17.657 5.304c-3.124-3.073-8.189-3.073-11.313 0-3.124 3.074-3.124 8.057 0 11.13l5.656 5.565 5.657-5.565c3.124-3.073 3.124-8.056 0-11.13zm-5.657 8.195c-.668 0-1.295-.26-1.768-.732-.975-.975-.975-2.561 0-3.536.472-.472 1.1-.732 1.768-.732s1.296.26 1.768.732c.975.975.975 2.562 0 3.536-.472.472-1.1.732-1.768.732z" />
        </svg>
      </div>
      {/* 等待期間的文字提示：正在定位 / 正在取得座標 */}
      {isGettingLocation || isGeocodingAddress ? (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1001,
            pointerEvents: "none",
            padding: "10px 16px",
            borderRadius: 8,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "#fff",
            fontSize: 14,
            fontWeight: 500,
          }}>
          {isGettingLocation ? "正在定位…" : "正在取得座標…"}
        </div>
      ) : null}
      </div>
      {geocodeError ? (
        <div style={{ padding: "8px 12px", fontSize: 14, color: "#c00", fontWeight: 600, background: "#fff" }}>
          {geocodeError}，可手動移動地圖定位。
        </div>
      ) : null}
    </div>
  );
};

export default MapPicker;
